import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";

export const fmt = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(b / 1024))}KB`);
export const clamp01 = (n) => Math.max(0, Math.min(1, n));
export const noop = () => {};
export const rectStyle = (r) => ({
  left: `${r.x0 * 100}%`,
  top: `${r.y0 * 100}%`,
  width: `${(r.x1 - r.x0) * 100}%`,
  height: `${(r.y1 - r.y0) * 100}%`,
});

export const PageThumb = ({ page, rects, editable, onAdd, onRemove, compact = false, rectClassName = null, rectLabel = "" }) => {
  const boxRef = useRef(null);
  const [draft, setDraft] = useState(null);

  const point = (e) => {
    const b = boxRef.current.getBoundingClientRect();
    return { x: clamp01((e.clientX - b.left) / b.width), y: clamp01((e.clientY - b.top) / b.height) };
  };

  const start = (e) => {
    if (!editable) return;
    e.preventDefault();
    boxRef.current.setPointerCapture(e.pointerId);
    const p = point(e);
    setDraft({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };

  const move = (e) => {
    if (!draft) return;
    const p = point(e);
    setDraft((d) => ({ ...d, x1: p.x, y1: p.y }));
  };

  const finish = () => {
    if (!draft) return;
    const r = {
      x0: Math.min(draft.x0, draft.x1),
      y0: Math.min(draft.y0, draft.y1),
      x1: Math.max(draft.x0, draft.x1),
      y1: Math.max(draft.y0, draft.y1),
    };
    setDraft(null);
    if (r.x1 - r.x0 > 0.015 && r.y1 - r.y0 > 0.015) onAdd(page.page, r);
  };

  return (
    <div className={`${compact ? "w-28 shrink-0" : "w-full"}`} data-testid={`thumbnail-page-${page.page}`}>
      <div
        ref={boxRef}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={finish}
        onPointerCancel={() => setDraft(null)}
        className={`relative overflow-hidden rounded-md border border-hairline bg-white shadow-[0_2px_16px_-8px_rgba(0,0,0,0.12)] ${editable ? "cursor-crosshair touch-none" : ""}`}
      >
        <img src={`data:image/png;base64,${page.image}`} alt={`Page ${page.page}`} draggable={false} className="block w-full select-none pointer-events-none" />
        {rects.map((r, i) => (
          <button
            key={i}
            type="button"
            data-testid={rectClassName ? `sign-field-${page.page}-${i}` : `redaction-box-${page.page}-${i}`}
            title={rectClassName ? "Remove signature box" : "Remove blackout"}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onRemove(page.page, i); }}
            className={rectClassName || "absolute bg-ink/90 border border-azure/70 hover:bg-[#FF3333] transition-colors duration-200"}
            style={rectStyle(r)}
          >
            {rectLabel ? <span className="font-mono text-[8px] uppercase tracking-[0.15em] text-azure pointer-events-none">{rectLabel}</span> : null}
          </button>
        ))}
        {draft && <div className={`absolute pointer-events-none ${rectClassName ? "bg-azure/15 border-2 border-dashed border-azure" : "bg-ink/70 border border-azure"}`} style={rectStyle({
          x0: Math.min(draft.x0, draft.x1),
          y0: Math.min(draft.y0, draft.y1),
          x1: Math.max(draft.x0, draft.x1),
          y1: Math.max(draft.y0, draft.y1),
        })} />}
      </div>
      <div className="flex items-center justify-between pt-1.5">
        <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-mist">Page {page.page}</span>
        {rects.length > 0 && <span className="font-mono text-[9px] text-azure">{rects.length}</span>}
      </div>
    </div>
  );
};

export const SizeTicker = ({ from, to }) => {
  const [val, setVal] = useState(from);
  useEffect(() => {
    const start = performance.now();
    const dur = 900;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to]);
  return <span className="text-ink tabular-nums" data-testid="size-ticker">{fmt(val)}</span>;
};

export const Panel = ({ children, testid }) => (
  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    className="px-5 pt-4 overflow-hidden" data-testid={testid}>
    {children}
  </motion.div>
);
